import React, { FC, useEffect, useRef, useState } from 'react';
import { ref, onValue, off, query, orderByChild, limitToLast } from 'firebase/database';
import { database } from '../../lib/firebase';

export interface FallAlert {
  alertId: string;
  seniorId: string;
  seniorName: string;
  impactMagnitude: number;
  impactTime: string;
  location?: {
    latitude: number;
    longitude: number;
    accuracy: number;
  };
  timestamp: string;
  _createdAt?: number;
}

export interface DistressAlert {
  alertId: string;
  seniorId: string;
  seniorName: string;
  distressLabel: string;
  detectedAt: string;
  source: string;
  timestamp: string;
  _createdAt?: number;
}

interface FallDetectionAlertProps {
  caregiverId?: string;
  onAlertReceived?: (alert: FallAlert) => void;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const FallDetectionAlert: FC<FallDetectionAlertProps> = ({ onAlertReceived }) => {
  const [alerts, setAlerts] = useState<FallAlert[]>([]);
  const [distressAlerts, setDistressAlerts] = useState<DistressAlert[]>([]);
  const [playingSound, setPlayingSound] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const onAlertReceivedRef = useRef(onAlertReceived);
  useEffect(() => { onAlertReceivedRef.current = onAlertReceived; });
  const audioContextRef = useRef<AudioContext | null>(null);

  // Listen for fall alerts from Firebase Realtime Database
  useEffect(() => {
    const fallsRef = query(
      ref(database, 'falls'),
      orderByChild('_createdAt'),
      limitToLast(20)
    );

    onValue(fallsRef, (snapshot) => {
      setIsConnected(true);
      snapshot.forEach((child) => {
        const alert = child.val() as FallAlert;
        const createdAt = alert._createdAt || 0;
        if (Date.now() - createdAt < FIVE_MINUTES_MS) {
          setAlerts((prev) => {
            if (prev.find((a) => a.alertId === alert.alertId)) return prev;
            console.log('🚨 Fall alert received from Firebase:', alert.seniorName, alert.impactMagnitude + 'g');
            playAlertSound();
            if (onAlertReceivedRef.current) onAlertReceivedRef.current(alert);
            showBrowserNotification(alert);
            return [alert, ...prev];
          });
        }
      });
    }, (error) => {
      // Fail quietly. `falls` is denied by the RTDB rules deployed 2026-08-16
      // (fall detection is a Wave 2 feature, off at launch), so this fires on
      // every mount. It is an expected denial, not a fault to report: a
      // console.error here is noise a caregiver's browser does not need and
      // that buries real errors during support calls.
      //
      // Kept as a debug-only line so the listener is still diagnosable when
      // FALL_DETECTION_ENABLED is flipped on and a denial WOULD be a fault.
      if (process.env.NODE_ENV === 'development') {
        console.debug('[FallDetectionAlert] falls listener inactive:', error.message);
      }
      setIsConnected(false);
    });

    return () => off(fallsRef);
  }, []);

  // Listen for distress alerts from Firebase Realtime Database
  useEffect(() => {
    const distressRef = query(
      ref(database, 'distress'),
      orderByChild('_createdAt'),
      limitToLast(20)
    );

    onValue(distressRef, (snapshot) => {
      snapshot.forEach((child) => {
        const alert = child.val() as DistressAlert;
        const createdAt = alert._createdAt || 0;
        if (Date.now() - createdAt < FIVE_MINUTES_MS) {
          setDistressAlerts((prev) => {
            if (prev.find((a) => a.alertId === alert.alertId)) return prev;
            console.log('🆘 Distress alert received from Firebase:', alert.seniorName, alert.distressLabel);
            playAlertSound();
            if ('Notification' in window && Notification.permission === 'granted') {
              const notif = new Notification('🆘 Distress Detected!', {
                body: `${alert.seniorName} — ${alert.distressLabel} detected`,
                icon: '/logo192.png',
                tag: alert.alertId,
                requireInteraction: true,
              });
              notif.onclick = () => { window.focus(); notif.close(); };
            }
            return [alert, ...prev];
          });
        }
      });
    }, (error) => {
      // Same as the `falls` listener above — this one had NO error callback at
      // all, so the Firebase SDK logged the permission denial itself, as an
      // unhandled listener error the app could not suppress. Supplying a
      // callback is what makes it quiet; the callback's own body is
      // deliberately near-silent.
      if (process.env.NODE_ENV === 'development') {
        console.debug('[FallDetectionAlert] distress listener inactive:', error.message);
      }
    });

    return () => off(distressRef);
  }, []);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const playAlertSound = () => {
    setPlayingSound(true);
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    const audioContext = audioContextRef.current;
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);

    setTimeout(() => setPlayingSound(false), 500);
  };

  const showBrowserNotification = (alert: FallAlert) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('🚨 Fall Detected!', {
        body: `${alert.seniorName} may have fallen. Impact: ${alert.impactMagnitude.toFixed(1)}g`,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: alert.alertId,
        requireInteraction: true,
        vibrate: [200, 100, 200],
      });
      notification.onclick = () => { window.focus(); notification.close(); };
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      await fetch(`${apiUrl}/fall-detection/${alertId}/acknowledge`, { method: 'POST' });
      setAlerts((prev) => prev.filter((a) => a.alertId !== alertId));
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
      // Remove locally even if server call fails
      setAlerts((prev) => prev.filter((a) => a.alertId !== alertId));
    }
  };

  const markFalseAlarm = async (alertId: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      await fetch(`${apiUrl}/fall-detection/${alertId}/false-alarm`, { method: 'POST' });
      setAlerts((prev) => prev.filter((a) => a.alertId !== alertId));
    } catch (error) {
      console.error('Failed to mark false alarm:', error);
      setAlerts((prev) => prev.filter((a) => a.alertId !== alertId));
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getGoogleMapsUrl = (location: { latitude: number; longitude: number }) =>
    `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;

  return (
    <>
      {/* Fall Detection Active badge hidden for launch
      {isConnected && (
        <div className="fixed bottom-4 right-4 z-40 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
          🟢 Fall Detection Active
        </div>
      )}
      */}

      {/* Distress alert popups (orange) */}
      {distressAlerts.length > 0 && (
        <div className="fixed z-[1100] space-y-3 pointer-events-none
                        top-3 right-3 left-3 sm:left-auto sm:top-4 sm:right-4
                        sm:w-[360px] max-w-[calc(100vw-1.5rem)]">
          {distressAlerts.map((alert) => (
            <div
              key={alert.alertId}
              className="bg-orange-500 text-white rounded-xl shadow-2xl p-4 animate-slideInRight pointer-events-auto"
              role="alert"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🆘</span>
                  <h3 className="font-bold text-lg">Distress Detected!</h3>
                </div>
                <button
                  onClick={() => setDistressAlerts((prev) => prev.filter((a) => a.alertId !== alert.alertId))}
                  className="text-white hover:text-orange-200 transition-colors"
                  aria-label="Dismiss"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="space-y-1 text-sm">
                <p className="font-semibold text-base">{alert.seniorName}</p>
                <p>Sound detected: <strong>{alert.distressLabel}</strong></p>
                <p className="text-orange-100">{formatTime(alert.detectedAt)}</p>
              </div>
              <button
                onClick={() => setDistressAlerts((prev) => prev.filter((a) => a.alertId !== alert.alertId))}
                className="mt-3 w-full bg-white text-orange-600 font-semibold py-2 px-4 rounded-lg hover:bg-orange-50 transition-colors"
              >
                Acknowledge
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Fall alert popups (red) */}
      {alerts.length > 0 && (
        <div className="fixed z-[1100] space-y-3 pointer-events-none
                        top-3 right-3 left-3 sm:left-auto sm:top-4 sm:right-4
                        sm:w-[360px] max-w-[calc(100vw-1.5rem)]">
          {alerts.map((alert) => (
            <div
              key={alert.alertId}
              className={`bg-red-600 text-white rounded-xl shadow-2xl p-4 animate-slideInRight pointer-events-auto ${
                playingSound ? 'ring-4 ring-red-400' : ''
              }`}
              role="alert"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-2xl animate-bounce">emergency</span>
                  <h3 className="font-bold text-lg">Fall Detected!</h3>
                </div>
                <button
                  onClick={() => acknowledgeAlert(alert.alertId)}
                  className="text-white hover:text-red-200 transition-colors"
                  aria-label="Dismiss alert"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-semibold text-base">{alert.seniorName}</p>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">schedule</span>
                  <span>{formatTime(alert.impactTime)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">speed</span>
                  <span>Impact: {alert.impactMagnitude.toFixed(2)}g</span>
                </div>
                {alert.location && (
                  <a
                    href={getGoogleMapsUrl(alert.location)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-red-200 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">location_on</span>
                    <span className="underline">View Location</span>
                  </a>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => acknowledgeAlert(alert.alertId)}
                  className="flex-1 bg-white text-red-600 font-semibold py-3 px-3 sm:py-2 sm:px-4 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Acknowledge
                </button>
                <button
                  onClick={() => markFalseAlarm(alert.alertId)}
                  className="flex-1 bg-red-700 text-white font-semibold py-3 px-3 sm:py-2 sm:px-4 rounded-lg hover:bg-red-800 transition-colors"
                >
                  False Alarm
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default FallDetectionAlert;
